import { useState } from 'react';
import { http } from '@/lib/api';
import { X, ShieldCheck, KeyRound, AlertCircle } from 'lucide-react';

/**
 * Step-up re-authentication modal.
 *
 * Usage:
 *   const [stepup, setStepup] = useState(null);
 *   const doSensitiveAction = async () => {
 *     try { await http.post(...) } catch (e) {
 *       if (e?.response?.status === 403 && e.response.data?.detail?.code?.startsWith('stepup_')) {
 *         setStepup({ purpose: e.response.data.detail.purpose,
 *                     onSuccess: () => { setStepup(null); doSensitiveAction(); } });
 *       } else throw e;
 *     }
 *   };
 *   ...
 *   <StepUpModal open={!!stepup} purpose={stepup?.purpose} onSuccess={stepup?.onSuccess} onClose={() => setStepup(null)} />
 */
export default function StepUpModal({ open, purpose, has2FA, onSuccess, onClose }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await http.post('/auth/re-authenticate', { password, code: has2FA ? code : undefined, purpose });
      setPassword(''); setCode('');
      onSuccess?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Re-authentication failed. Try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="stepup-modal">
      <div className="w-full max-w-md bg-white rounded-3xl p-7 card-soft">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gold/15 flex items-center justify-center">
              <ShieldCheck size={18} className="text-gold" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-gold font-semibold">Verify identity</p>
              <h2 className="font-heading text-xl">Sensitive action</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-navy/50 hover:text-navy" aria-label="Cancel">
            <X size={18} />
          </button>
        </div>
        <p className="mt-3 text-sm text-navy/60 leading-relaxed">
          Re-enter your password{has2FA ? ' and a fresh 2FA code' : ''} to continue.
          This unlocks the action for the next 5 minutes.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input
            type="password" required autoFocus
            value={password} onChange={(e)=>setPassword(e.target.value)}
            placeholder="Current password"
            className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
            data-testid="stepup-password"
          />
          {has2FA && (
            <input
              inputMode="numeric" pattern="[0-9]*" required
              value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit 2FA code"
              className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold tracking-[0.4em] font-mono text-center"
              data-testid="stepup-code"
            />
          )}
          {error && (
            <div className="text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 shrink-0"/> {error}
            </div>
          )}
          <button
            type="submit" disabled={loading}
            className="w-full rounded-full bg-navy text-white py-3 font-medium hover:bg-navy/90 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="stepup-submit"
          >
            <KeyRound size={14}/> {loading ? 'Verifying…' : 'Verify & continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
